<template>
    <div :class="[
        'token-price',
        {
            'token-price--no-price': isPriceExistOrNeeded,
            'token-price--is-buy-sell': isBuySell,
        }
    ]"
    >
        <div class="token-price__value">
            <i :class="[
                'token-price__icon icon-graph', {
                }]"
            />
            <span>{{ tokenPrice.value }}</span>
        </div>

        <div v-if="isPriceAvailable"
            class="token-price__prices"
        >
            <div v-if="tokenPrice.evolutionPercentage"
                :class="[
                    'token-price__prices-fluctuation',
                    {
                        'token-price__prices-fluctuation--down' : parseFloat(tokenPrice.evolutionPercentage) < 0
                    }
                ]"
            >
                {{ tokenPrice.evolutionPercentage }}
            </div>

            <div v-if="isChzAllowed"
                class="token-price__prices-price"
            >
                {{ tokenPrice.altValue }}
            </div>
        </div>

        <button v-for="(item, index) in buttons"
            :key="index"
            :class="`button token-price__button ${item.class} ${item.disabled || !isPriceAvailable ? 'button--disabled' : ''}`"
            @click="buttonClicked(item)"
        >
            {{ item.label }}
        </button>

        <button
            v-if="isBuyTestButtonDisplay"
            class="button button--orange"
            @click="redirectToGateway"
        >
            Buy TEST
        </button>
    </div>
</template>

<script>
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Mixins
import tokenItemMixin from '@webapp/helpers/token-item-mixin.js';

import {
    getFanTokenDirectPurchaseGatewayURL,
} from '@shared/utils/fan-token-direct-purchase';

import authStoreTypes from '@shared/store/modules/auth/types';

export default {
    name: 'token-price',
    mixins: [tokenItemMixin],
    props: {
        partner: {
            type: Object,
            default: () => ({}),
            required: true,
        },
        buttons: {
            type: Array,
            default: () => [],
            required: true,
        },
    },
    data() {
        return {
            tokenPrice: {
                value: 0,
                rawValue: 0,
                altValue: 0,
                info: '',
                state: '',
            },
        };
    },
    computed: {
        ...mapGetters('marketplace', ['marketByPartnerId']),

        ...mapGetters('wallet', ['isChzAllowed']),

        ...mapGetters([
            'userId',
            'device',
        ]),
        ...mapGetters('auth', ['getSessionAuthToken']),

        isPriceAvailable() {
            return parseInt(this.tokenPrice.rawValue) >= 0;
        },

        isPriceExistOrNeeded() {
            return !this.isPriceAvailable || (!this.isChzAllowed && !this.tokenPrice.evolutionPercentage);
        },

        isBuySell() {
            return this.buttons.length > 1;
        },

        market() {
            return this.marketByPartnerId(this.partner.id);
        },

        isBuyTestButtonDisplay() {
            // Button used to test the new FanToken Direct Purchase Flow. Will be removed when capsicum will be released.
            const disallowedEnvironments = ['production', 'staging', 'integration'];

            if (!disallowedEnvironments.includes(process.env.VUE_APP_ENV)) {
                return this.buttons.some((button) => button.class === 'button--orange');
            }

            return false;
        },
    },
    methods: {
        ...mapActions('auth', {
            fetchSessionAuthToken: authStoreTypes.GET_SESSION_AUTH_TOKEN,
        }),

        async redirectToGateway() {
            await this.fetchSessionAuthToken();
            let isJSON = false;

            try {
                isJSON = JSON.parse([this.device?.uuid])?.data;
            } catch (err) {
                // Err is already controled with boolean in case user didnt accept cookies
            }

            const deviceId = isJSON ? isJSON : this.device?.uuid;

            const queryParams = {
                'purchaseAmount': 25,
                'fanTokenCurrencyId': this.partner.currencyId,
                'generatedCode': this.getSessionAuthToken,
                'userId': this.userId,
                'deviceUid': deviceId,
                'deviceName': this.device.name,
                'webApp': true,
            };
            const redirectUrl = getFanTokenDirectPurchaseGatewayURL(queryParams);

            window.location.href = redirectUrl;
        },

        buttonClicked(item) {
            this.$router.push(item.route);
        },
    },
};
</script>

<style lang="scss" scoped>
.token-price {
    min-height: 5rem;
    text-align: right;
    color: $cyprus-color;

    @include min-width(lg) {
        padding-left: 2rem;
        background: $white-color;
        border-top-left-radius: 2.5rem;
        border-bottom-left-radius: 2.5rem;
        border-top-right-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
        display: grid;
        grid-template-columns: repeat(3, auto);
        grid-template-rows: 60%, 40%;
    }

    &--no-price {
        @include min-width(lg) {
            .token-price {
                &__icon {
                    margin-top: -1.25rem;
                }

                &__value {
                    grid-row: 1 / 3;
                }
            }
        }
    }

    &__icon {
        margin-right: 0.5rem;
        font-size: 2.5rem;

        @include min-width(lg) {
            font-size: 3.25rem;
            margin: 0.5rem 1rem -1.25rem 0;
        }

        @include min-width(xl) {
            margin: 0.5rem 2.5rem -1.25rem 0;
        }
    }

    &__value,
    &__prices {
        @include min-width(md) {
            padding-right: 1.5rem;
        }
    }

    &__value {
        padding-top: 0.25rem;
        font-size: 2rem;
        font-weight: bold;
        display: flex;
        align-items: center;

        span {
            @include min-width(md) {
                min-width: 12.5rem;
            }
        }

        @include min-width(lg) {
            grid-column: 1;
            grid-row: 1;
        }
    }

    &__prices {
        font-size: 1.3rem;
        padding-bottom: 0.1rem;
        display: flex;
        margin-left: 2.75rem;
        grid-column: 1;
        grid-row: 2;

        @include min-width(md) {
            margin-left: 2.75rem;
        }

        @include min-width(lg) {
            margin-left: 3.75rem;
        }

        @include min-width(xl) {
            margin-left: 5.75rem;
        }

        &-price {
            margin-left: auto;

            @include min-width(lg) {
                grid-column: 1;
                grid-row: 2;
            }
        }

        &-fluctuation {
            color: $lima-color;
            margin-left: auto;

            &--down {
                color: $monza-color;
            }
        }
    }

    &__button {
        display: none;
        grid-column: 2;
        grid-row: 1 / 3;
        margin-left: 2rem;
        padding: 0 2.5rem;
        height: 5rem;

        @include min-width(lg) {
            display: flex;
        }

        @include min-width(xl) {
            padding: 0 5rem;
        }

        & + .token-price__button {
            grid-column: 3;
            margin-left: 2rem;
        }
    }
}
</style>